import { FC, useEffect, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  toggleControlBarShowing,
  updateControlBarComponents,
} from '../../../../../redux/ui/controlbar/actions';
import { useNavigate } from 'react-router-dom';
import BackToOverviewBtn from '../../components/buttons/BackToOverviewBtn';
import { toast } from '../../../../toast/Toast';
import { RaidrV2RoutePaths } from '../../../../../routes/page-routes';
import useRouteParams from '../../../../../hooks/useRouteParams';
import TokenService from '../../../../../services/token-service';
import { useGetKidOverviewData } from '../../../../../api/kids/hooks/queries';
import { ReviewKidDocumentPayload } from '../../../../../api/kids/types';
import {
  useReviewKidDocument,
  useRevokeReviewKid,
  useUpdateOverviewStatus,
} from '../../../../../api/kids/hooks/mutations';
import { useQueryClient } from '@tanstack/react-query';
import { useForm } from 'react-hook-form';
import GenericModal from '../../../../ui/GenericModal';
import Pill from '../../../../ui/Pill';
import { Button, CircularProgress, Tooltip } from '@mui/material';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import UndoIcon from '@mui/icons-material/Undo';
import { clientNameSelector } from '../../../../../redux/auth/selectors';
import { useFundAndShareClassName } from '../hooks/useFundAndShareClassName';
import {
  KidDocumentStatus,
  KID_STATUS_LABEL,
  KID_STATUS_TO_PILL,
} from '../types/KidEditorV2.types';
import { KID_STATUS_COLOR } from '../../../../../utilities/colorUtilities';
import KidsComparisonView from './KidsComparisonView';
import ReviewMoreDialogV2 from './ReviewMoreDialogV2';

const REVOKE_COLOR = '#6b7280'; // gray-500 — communicates "undo" rather than implying a status

const KidsReviewV2: FC = () => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { user_client_id: clientId } = TokenService.getUser()!;
  const clientName = useSelector(clientNameSelector);
  const queryClient = useQueryClient();

  const [pendingAction, setPendingAction] = useState<'accept' | 'reject' | null>(null);
  const [showRejectionModal, setShowRejectionModal] = useState(false);
  const [showReviewMoreModal, setShowReviewMoreModal] = useState(false);
  const [pendingShowReviewMoreModal, setPendingShowReviewMoreModal] = useState(false);

  const fundId = useRouteParams('fundId')['fundId'] || '';
  const shareClassId = useRouteParams('shareClassId')['shareClassId'] || '';
  const documentLanguage =
    useRouteParams('documentLanguage')['documentLanguage'] || 'English';
  const compareVersion =
    useRouteParams('compareVersion')['compareVersion'] || '';
  const targetVersion = useRouteParams('targetVersion')['targetVersion'] || '';

  const reviewKidDocumentMutation = useReviewKidDocument();
  const revokeReviewKidMutation = useRevokeReviewKid();
  const updateOverviewStatusMutation = useUpdateOverviewStatus();

  // React Hook Form for rejection reason
  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<{ rejectionReason: string }>({
    mode: 'onChange',
  });

  // Fetch fund and share class data for names
  const { fundNameFull, shareClassName, isLoading } = useFundAndShareClassName({
    clientId,
    fundId,
    shareClassId,
    pageKey: 'kid-review-v2',
  });

  const displayFundName = fundNameFull || (isLoading ? '…' : fundId);
  const displayShareClassName = shareClassName || (isLoading ? '…' : shareClassId);

  // Fetch overview data to get document status
  const { data: overviewData, isFetching: isOverviewFetching } = useGetKidOverviewData(clientId);

  // Find the matching document from overview data
  const matchingDocument = overviewData?.find(
    (item) =>
      item.fund_id === fundId &&
      item.share_class_id === shareClassId &&
      item.document_language === documentLanguage,
  );

  const documentStatus = matchingDocument?.status;
  const rejectionReason = matchingDocument?.description;
  const isRejected = documentStatus === KidDocumentStatus.Rejected;
  const isReviewed = documentStatus === KidDocumentStatus.Reviewed;

  const goToOverview = useCallback(() => {
    navigate(RaidrV2RoutePaths.KID_OVERVIEW_V2, { state: { fundId, shareClassId } });
  }, [navigate, fundId, shareClassId]);

  // Set up control bar on mount
  useEffect(() => {
    dispatch(
      updateControlBarComponents([
        <BackToOverviewBtn
          backToOverview={goToOverview}
          dontSetPosition
          key="back_to_overview"
        />,
        <div className="ml-4 flex min-w-0 flex-1 items-center justify-between gap-4 pr-2">
          <h6 className="truncate text-xl font-semibold text-mainblue">
            {displayFundName} - {displayShareClassName} (v{targetVersion} ↔ v{compareVersion}
            )
          </h6>
          {documentStatus && (
            <>
              {isRejected && rejectionReason ? (
                <Tooltip
                  title={
                    <div>
                      <p className="mb-1 text-sm font-semibold text-red-800">
                        Rejection Reason:
                      </p>
                      <p className="text-sm text-red-700">
                        {rejectionReason || 'Document has been rejected'}
                      </p>
                    </div>
                  }
                  arrow
                  placement="bottom"
                  slotProps={{
                    tooltip: {
                      className:
                        'rounded-lg border border-red-300 bg-red-50 p-4 shadow-lg',
                    },
                    arrow: {
                      sx: {
                        color: KID_STATUS_COLOR[KidDocumentStatus.Rejected],
                        '&::before': {
                          border: `1px solid ${KID_STATUS_COLOR[KidDocumentStatus.Rejected]}`,
                          boxSizing: 'border-box',
                        },
                      },
                    },
                  }}
                >
                  <span
                    className="flex cursor-help items-center gap-1.5 rounded px-3 py-1 text-sm font-semibold text-white transition-opacity hover:opacity-90"
                    style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Rejected] }}
                  >
                    Rejected
                    <InfoOutlinedIcon className="h-3.5 w-3.5" />
                  </span>
                </Tooltip>
              ) : (
                <Pill
                  status={(KID_STATUS_TO_PILL[documentStatus as KidDocumentStatus] as any) ?? 'neutral'}
                  className="rounded px-2 text-lg font-semibold"
                >
                  {KID_STATUS_LABEL[documentStatus as KidDocumentStatus] ?? documentStatus}
                </Pill>
              )}
            </>
          )}
        </div>,
      ]),
    );
    dispatch(toggleControlBarShowing(true));

    return () => {
      dispatch(updateControlBarComponents([]));
      dispatch(toggleControlBarShowing(false));
    };
  }, [
    dispatch,
    goToOverview,
    displayFundName,
    displayShareClassName,
    compareVersion,
    targetVersion,
    documentStatus,
    rejectionReason,
    isRejected,
    isReviewed,
  ]);

  const basePayload = {
    client_id: clientId,
    fund_id: fundId!,
    share_class_id: shareClassId!,
    document_language: documentLanguage,
  };

  const submitReview = (
    statusChangeTo: KidDocumentStatus.Reviewed | KidDocumentStatus.Rejected,
    reason?: string,
  ) => {
    const payload: ReviewKidDocumentPayload = {
      ...basePayload,
      status_change_to: statusChangeTo,
      ...(reason && { message: reason }),
    };
    reviewKidDocumentMutation.mutate(payload, {
      onSuccess: () => {
        queryClient.invalidateQueries({ queryKey: ['kid-overview-data'] });
        toast({
          title: 'Document reviewed',
          message: `Document marked as ${statusChangeTo}`,
          type: 'success',
        });
        if (statusChangeTo === KidDocumentStatus.Rejected) {
          setShowRejectionModal(false);
          reset();
        }
        setPendingShowReviewMoreModal(true);
      },
      onError: (error) => {
        toast({
          title: 'Review failed',
          message: `${(error as Error)?.message || 'Unknown error'}`,
          type: 'error',
        });
      },
    });
  };

  const onSubmitRejection = (data: { rejectionReason: string }) => {
    setPendingAction('reject');
    submitReview(KidDocumentStatus.Rejected, data.rejectionReason);
  };

  const handleRevokeStatus = () => {
    if (isRejected) {
      updateOverviewStatusMutation.mutate({
        ...basePayload,
        status_change_to: KidDocumentStatus.Edited as 'edited',
      });
    } else {
      revokeReviewKidMutation.mutate(basePayload);
    }
  };

  const isActionPending =
    reviewKidDocumentMutation.isPending ||
    revokeReviewKidMutation.isPending ||
    updateOverviewStatusMutation.isPending;

  // Clear pendingAction only once both the mutation AND the triggered refetch have settled
  useEffect(() => {
    if (pendingAction !== null && !isActionPending && !isOverviewFetching) {
      setPendingAction(null);
    }
  }, [isActionPending, isOverviewFetching, pendingAction]);

  useEffect(() => {
    if (
      pendingShowReviewMoreModal &&
      !isOverviewFetching &&
      overviewData !== undefined
    ) {
      setPendingShowReviewMoreModal(false);
      const other =
        overviewData.filter(
          (item) =>
            item.status === KidDocumentStatus.Edited &&
            !(
              item.fund_id === fundId &&
              item.share_class_id === shareClassId &&
              item.document_language === documentLanguage
            ),
        );
      if (other.length > 0) {
        setShowReviewMoreModal(true);
      }
    }
  }, [pendingShowReviewMoreModal, isOverviewFetching, overviewData, fundId, shareClassId, documentLanguage]);

  const isAcceptLoading = (isActionPending || isOverviewFetching) && pendingAction === 'accept';
  const isRejectLoading = (isActionPending || isOverviewFetching) && pendingAction === 'reject';

  const handleRejectOrRevoke = () => {
    if (isRejected) {
      // Revoking: mutation fires immediately so set loading now
      setPendingAction('reject');
      handleRevokeStatus();
    } else {
      // Opening modal: mutation fires on submit so loading is set in onSubmitRejection
      setShowRejectionModal(true);
    }
  };

  const handleAcceptOrRevoke = () => {
    setPendingAction('accept');
    isReviewed ? handleRevokeStatus() : submitReview(KidDocumentStatus.Reviewed);
  };

  const reviewActionButtons = (
    <div className="flex items-center gap-1.5">
      {!isRejected && (
        <Tooltip title={isReviewed ? 'Revoke acceptance and return to review.' : 'Mark document as reviewed and ready to publish.'} placement="top" arrow>
          <span>
            <Button
              onClick={handleAcceptOrRevoke}
              disabled={isActionPending}
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
              style={{ backgroundColor: isReviewed ? REVOKE_COLOR : KID_STATUS_COLOR[KidDocumentStatus.Reviewed] }}
              startIcon={
                isAcceptLoading
                  ? <CircularProgress size={12} color="inherit" />
                  : isReviewed
                    ? <UndoIcon style={{ fontSize: 14 }} />
                    : <CheckCircleIcon style={{ fontSize: 14 }} />
              }
            >
              <span className="text-xs">
                {isAcceptLoading
                  ? (isReviewed ? 'Revoking…' : 'Accepting…')
                  : (isReviewed ? 'Revoke Acceptance' : 'Accept')}
              </span>
            </Button>
          </span>
        </Tooltip>
      )}
      {!isReviewed && (
        <Tooltip title={isRejected ? 'Revoke rejection and return to review.' : 'Reject and send back for editing.'} placement="top" arrow>
          <span>
            <Button
              onClick={handleRejectOrRevoke}
              disabled={isActionPending}
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
              style={{ backgroundColor: isRejected ? REVOKE_COLOR : KID_STATUS_COLOR[KidDocumentStatus.Rejected] }}
              startIcon={
                isRejectLoading
                  ? <CircularProgress size={12} color="inherit" />
                  : isRejected
                    ? <UndoIcon style={{ fontSize: 14 }} />
                    : <CancelIcon style={{ fontSize: 14 }} />
              }
            >
              <span className="text-xs">
                {isRejectLoading
                  ? (isRejected ? 'Revoking…' : 'Rejecting…')
                  : (isRejected ? 'Revoke Rejection' : 'Reject')}
              </span>
            </Button>
          </span>
        </Tooltip>
      )}
    </div>
  );

  return (
    <>
      <KidsComparisonView
        pageKey="kid-review-v2"
        clientName={clientName ?? ''}
        toolbarRight={reviewActionButtons}
      />

      <GenericModal
        isOpen={showRejectionModal}
        setIsOpen={setShowRejectionModal}
        title="Reject Document"
        className="max-w-lg"
      >
        <form onSubmit={handleSubmit(onSubmitRejection)} className="space-y-4">
          <div>
            <label
              htmlFor="rejectionReason"
              className="mb-2 block text-sm font-medium text-gray-700"
            >
              Reason for Rejection <span className="text-red-500">*</span>
            </label>
            <textarea
              id="rejectionReason"
              {...register('rejectionReason', {
                required: 'Rejection reason is required',
              })}
              rows={4}
              className={`w-full rounded-md border ${errors.rejectionReason
                ? 'border-red-500 focus:border-red-500 focus:ring-red-500'
                : 'border-gray-300 focus:border-blue-500 focus:ring-blue-500'
                } px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-1`}
              placeholder="Please provide a reason for rejecting this document..."
            />
            {errors.rejectionReason && (
              <p className="mt-1 text-sm text-red-600">
                {errors.rejectionReason.message}
              </p>
            )}
          </div>

          <div className="flex justify-end gap-1.5 pt-4">
            <Button
              type="button"
              onClick={() => {
                setShowRejectionModal(false);
                reset();
              }}
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !text-gray-700 bg-gray-200 hover:bg-gray-300 disabled:!bg-gray-200 disabled:!opacity-40"
            >
              <span className="text-xs">Cancel</span>
            </Button>
            <Button
              type="submit"
              disabled={reviewKidDocumentMutation.isPending}
              variant="contained"
              disableElevation
              size="small"
              className="normal-case !text-white hover:brightness-90 disabled:!opacity-40"
              style={{ backgroundColor: KID_STATUS_COLOR[KidDocumentStatus.Rejected] }}
              startIcon={
                reviewKidDocumentMutation.isPending
                  ? <CircularProgress size={12} color="inherit" />
                  : <CancelIcon style={{ fontSize: 14 }} />
              }
            >
              <span className="text-xs">Submit Rejection</span>
            </Button>
          </div>
        </form>
      </GenericModal>

      <ReviewMoreDialogV2
        isOpen={showReviewMoreModal}
        setIsOpen={setShowReviewMoreModal}
        clientId={clientId}
        overviewData={overviewData ?? []}
        currentFundId={fundId}
        currentShareClassId={shareClassId}
        currentDocumentLanguage={documentLanguage}
        onReturnToOverview={goToOverview}
      />
    </>
  );
};

export default KidsReviewV2;
